import { useState } from 'react'

/**
 * Deterministic 0-based hash (0–359) of a string, for a stable placeholder
 * gradient. The same seed always yields the same hue, so a given album's
 * placeholder is consistent across renders and surfaces.
 *
 * @param seed - Stable seed string (album id or title).
 * @returns A hue value in the range [0, 360).
 */
function seedHue(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return hash % 360
}

/**
 * The seeded two-tone gradient for a placeholder, derived from the seed's hue.
 *
 * @param seed - Stable seed string (album id or title).
 * @returns A CSS `linear-gradient(...)` value.
 */
function gradientFor(seed: string): string {
  const hue = seedHue(seed)
  return `linear-gradient(140deg, hsl(${hue} 24% 26%), hsl(${(hue + 40) % 360} 30% 46%))`
}

interface CoverImageProps {
  /** The artwork URL, if any. A falsy value OR a load failure falls back to the gradient. */
  url?: string | null
  /** Stable seed for the deterministic placeholder gradient (album id or title). */
  seed: string
  /** Accessible name for the real artwork image. Pass '' when purely decorative. */
  alt: string
  /** Fixed square edge length in px. Provide this OR `fill` (not both). */
  size?: number
  /** Corner radius in px (fixed-size mode only). */
  radius?: number
  /** Fill the parent's box (h-full w-full) instead of rendering a fixed square. */
  fill?: boolean
  /** Extra classes applied to BOTH the <img> and the gradient fallback (rounding, blur, transitions). */
  className?: string
  /** Optional data-testid forwarded to the rendered <img> (placeholder gets `${testId}-placeholder`). */
  testId?: string
}

/**
 * Cover artwork that degrades gracefully: it renders the real image when a URL
 * is present and loads, and a deterministic seeded gradient placeholder when the
 * URL is missing **or fails to load** (404, network error). This is the single
 * owner of the `onError → gradient` behavior; every cover surface routes through
 * it so a dead URL never surfaces the browser's broken-image alt text.
 *
 * A failure is tracked per-URL, so if `url` later changes (e.g. a re-minted SAS
 * link) the new URL gets a fresh attempt rather than staying stuck on the fallback.
 *
 * @param props - {@link CoverImageProps}.
 * @returns The artwork <img> or the seeded gradient placeholder.
 */
export function CoverImage({
  url,
  seed,
  alt,
  size,
  radius,
  fill = false,
  className = '',
  testId,
}: CoverImageProps): JSX.Element {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const showImage = Boolean(url) && url !== failedUrl

  if (showImage) {
    return (
      <img
        src={url as string}
        alt={alt}
        data-testid={testId}
        loading="lazy"
        onError={() => setFailedUrl(url ?? null)}
        className={`object-cover ${fill ? 'h-full w-full' : 'shrink-0'} ${className}`}
        style={fill ? undefined : { width: size, height: size, borderRadius: radius }}
      />
    )
  }

  const background = gradientFor(seed)
  const placeholderTestId = testId ? `${testId}-placeholder` : undefined

  if (fill) {
    return (
      <div
        aria-hidden="true"
        data-testid={placeholderTestId}
        className={`relative h-full w-full overflow-hidden ${className}`}
        style={{ background, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.08)' }}
      >
        <div className="absolute inset-0 grid place-items-center opacity-90">
          <div className="grid aspect-square w-1/3 place-items-center rounded-full border border-white/50">
            <div className="aspect-square w-1/5 rounded-full bg-white/75" />
          </div>
        </div>
      </div>
    )
  }

  const edge = size ?? 0
  return (
    <div
      aria-hidden="true"
      data-testid={placeholderTestId}
      className={`relative shrink-0 overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.08)',
      }}
    >
      <div className="absolute inset-0 grid place-items-center opacity-90">
        <div
          className="grid place-items-center rounded-full"
          style={{ width: edge * 0.34, height: edge * 0.34, border: `${Math.max(1, edge * 0.03)}px solid rgba(255,255,255,.5)` }}
        >
          <div className="rounded-full" style={{ width: edge * 0.07, height: edge * 0.07, background: 'rgba(255,255,255,.75)' }} />
        </div>
      </div>
    </div>
  )
}
